/**
 * SystemDesignTab — the Admin page's brand-book surface for GROUP admins.
 *
 * The page is the JOURNEY a bundle takes, in the order it happens: make one
 * with an agent, try it in a sandbox conversation, then install it on a
 * tenant. It used to be four cards of equal weight with the tenant picker
 * first, which read as four unrelated settings and put the destructive step
 * (install, which restyles every agent at once) beside the harmless one.
 *
 * The install panels themselves are the super-admin components, unchanged —
 * only the API entrance differs (`apiBase`), so the two surfaces cannot drift
 * apart in look or behavior.
 *
 * The tenant list is passed in rather than fetched: AdminPage already holds
 * exactly this list (same GET /group-admin/tenants), so refetching it here
 * would cost a second round trip and, worse, let two copies disagree after a
 * tenant is created on the Tenants tab.
 */
import { useEffect, useState } from 'react';
import { Select } from 'antd';
import { useLanguage } from '@/hooks/useLanguage';
import { useAuth } from '@/hooks/useAuth';
import type { TenantHierarchy } from '@/types/admin';
import DesignSkillPanel from '@/modules/super_admin/design_system/components/DesignSkillPanel';
import BrandbookAgentCard from '@/modules/admin/components/BrandbookAgentCard';
import BrandTestCard from '@/modules/admin/components/BrandTestCard';
import { useFeature } from '@/hooks/useFeature';
// The wl-section styles the panels are designed against.
import '@/modules/super_admin/design_system/white-label-editor.css';

interface Props {
  /** Every tenant the signed-in admin manages (own + descendants). */
  tenants: TenantHierarchy[];
}

/** One numbered stage of the journey, with its own heading and cards. */
function Step({ index, title, hint, children }: {
  index: number; title: string; hint: string; children: React.ReactNode;
}) {
  return (
    <section className="wl-step" data-testid={`system-design-step-${index}`}>
      <div className="wl-step-head">
        <span className="wl-step-num" aria-hidden="true">{index}</span>
        <div>
          <h3 className="wl-step-title">{title}</h3>
          <p className="wl-step-hint">{hint}</p>
        </div>
      </div>
      <div className="wl-step-body">{children}</div>
    </section>
  );
}

export default function SystemDesignTab({ tenants }: Props) {
  const { t } = useLanguage();
  const { tenantId: ownTenantId } = useAuth();
  // The authoring half of this page exists only where the tenant has the
  // brand-book agent switched on; uploading a bundle never required it.
  const brandbookEnabled = useFeature('modules.brandbook');
  const [selected, setSelected] = useState<string | null>(null);

  // Start on the admin's own tenant when it is in scope (it always is for a
  // group admin), else the first tenant they manage. Re-runs only while
  // nothing is chosen, so it can never yank the picker out from under them.
  useEffect(() => {
    if (selected) return;
    const own = tenants.find(x => x.tenant_id === ownTenantId);
    setSelected(own?.tenant_id ?? tenants[0]?.tenant_id ?? null);
  }, [tenants, ownTenantId, selected]);

  // Numbered in the order they are shown: without the authoring agent the
  // page starts at "try a bundle somebody sent you", and calling that step 2
  // would leave a hole where step 1 should be.
  let step = 0;

  return (
    <div
      // The Admin page's .tab-content is overflow:hidden by design — each
      // tab brings its own scroller (the tables do). This tab had none, so
      // anything past the fold was simply CUT (field report, 2026-08-26).
      // flex:1 + minHeight:0 makes this the flex child that takes the tab's
      // height; overflowY makes it the scroller.
      className="wl-design-page"
      style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
      data-testid="system-design-tab">
      <header className="wl-page-head">
        <div>
          <h2 className="wl-page-title">{t('systemDesign.pageTitle')}</h2>
          <p className="wl-page-subtitle">{t('systemDesign.pageSubtitle')}</p>
        </div>
        <label className="wl-page-tenant">
          <span>{t('systemDesign.tenantPickerTitle')}</span>
          <Select
            style={{ width: 300 }}
            value={selected}
            options={tenants.map(x => ({
              value: x.tenant_id,
              label: x.tenant_code ? `${x.tenant_name} (${x.tenant_code})` : x.tenant_name,
            }))}
            onChange={setSelected}
            showSearch
            optionFilterProp="label"
            data-testid="system-design-tenant-picker"
          />
        </label>
      </header>

      {brandbookEnabled && (
        <Step index={++step}
              title={t('systemDesign.steps.createTitle')}
              hint={t('systemDesign.steps.createHint')}>
          <BrandbookAgentCard />
        </Step>
      )}

      {selected && (
        <Step index={++step}
              title={t('systemDesign.steps.tryTitle')}
              hint={t('systemDesign.steps.tryHint')}>
          <BrandTestCard key={`sandbox-${selected}`} tenantId={selected} />
        </Step>
      )}

      {selected && (
        <Step index={++step}
              title={t('systemDesign.steps.installTitle')}
              hint={t('systemDesign.steps.installHint')}>
          {/* key= remounts the panels on tenant switch so no state leaks
              from the previously selected tenant. */}
          <DesignSkillPanel key={`own-${selected}`} tenantId={selected} scope="own" apiBase="group-admin" />
          <DesignSkillPanel key={`default-${selected}`} tenantId={selected} scope="default" apiBase="group-admin" />
        </Step>
      )}
    </div>
  );
}
